/**
 * FILTER UX V2 (2026-07-08) — variant-B behavior for the Quince-style filter revamp A/B test.
 *
 * Loaded ONLY on v2 templates (gated in snippets/boost-pfs.liquid, between
 * boost-pfs-filter.js and boost-pfs-init.js — deferred scripts execute in document order,
 * so the boostPFSFilterConfig mutations and prototype wraps below land before
 * boost-pfs-init.js instantiates BoostPFS). Control sessions never load this file.
 *
 * Boost hooks used (verified in assets/boost-pfs-core.js / boost-pfs-filter.js):
 *   - boostPFSFilterConfig.general/.label merge into Settings before init
 *   - Filter.prototype.afterRender(data) — theme-owned, race-free post-render hook
 *   - FilterApi.setParam / FilterApi.applyFilter — programmatic filter mutation
 *   - .boost-pfs-filter-tree-mobile-open class on #boost-pfs-filter-tree (sheet state)
 *   - .boost-pfs-filter-show-result footer button (closes the tree, live count)
 */
(function () {
  'use strict';

  if (!document.querySelector('.mt-fv2')) return;
  if (typeof window.boostPFSFilterConfig === 'undefined') return;

  // Every visible facet becomes a pill in the swipeable rail (Quince-style — no cap).

  /* ---------- 1. Config overrides — MUST run before boost-pfs-init.js ---------- */
  boostPFSFilterConfig.general = boostPFSFilterConfig.general || {};
  // v2 owns stickiness in CSS (mt-filter-v2.css); Boost's JS sticky would fight it.
  boostPFSFilterConfig.general.stickyFilterOnMobile = false;
  boostPFSFilterConfig.general.stickyFilterOnDesktop = false;
  // Boost lazy-renders facet values beyond the first 24 only when its inner scrollbox
  // scrolls; the sheet removed that scrollbox (single scroller) and iOS never fired it
  // anyway — render every value up front (longest facet is ~170 values).
  boostPFSFilterConfig.general.scrollFirstLoadLength = 999;
  // DESKTOP GRID 2026 topbar (2026-08-05): Quince applies filters the moment a value is
  // clicked — no Apply button. requestInstantly only affects the horizontal dropdown
  // panels (vertical trees already instant-apply), and the H tree exists only on topbar
  // variant templates, so this is variant-scoped by construction; guarded anyway.
  if (document.querySelector('.mt-dgrid-topbar')) {
    boostPFSFilterConfig.general.requestInstantly = true;
  }
  boostPFSFilterConfig.label = boostPFSFilterConfig.label || {};
  boostPFSFilterConfig.label.refineMobile = 'Filter'; // Quince parity — icon carries "all"
  boostPFSFilterConfig.label.refine = 'Filters'; // sheet header title
  boostPFSFilterConfig.label.showResult = 'Show results';
  boostPFSFilterConfig.label.clearAll = 'Clear all';

  /* ---------- helpers ---------- */
  function track(name, params) {
    try {
      params = params || {};
      params.mt_variant = 'b';
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    } catch (e) { /* analytics must never break the UI */ }
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function toArray(v) {
    if (v === null || typeof v === 'undefined' || v === '') return [];
    return Array.isArray(v) ? v.slice() : [v];
  }

  function getQueryParams() {
    try {
      if (typeof window.Globals !== 'undefined' && window.Globals.queryParams) {
        return window.Globals.queryParams;
      }
    } catch (e) { /* fall through */ }
    return {};
  }

  // "material:silver" -> "Silver" · "10:25" on a price facet -> "$10 – $25" · "25:" -> "Over $25"
  function formatValue(key, raw) {
    var v = String(raw);
    var range = v.match(/^(\d*\.?\d*):(\d*\.?\d*)$/);
    if (range) {
      var money = /price/i.test(key) ? '$' : '';
      var lo = range[1], hi = range[2];
      if (/sale|percent/i.test(key)) return 'On Sale';
      if (lo && hi) return money + lo + ' – ' + money + hi;
      if (lo) return 'Over ' + money + lo;
      if (hi) return 'Under ' + money + hi;
      return v;
    }
    if (v.indexOf(':') > 0) v = v.slice(v.indexOf(':') + 1);
    v = v.replace(/[-_]+/g, ' ').trim();
    return v.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // Boost renders each facet group with a slug class, e.g. "boost-pfs-filter-option-material".
  // Skip the structural/display classes to isolate the slug.
  var OPTION_STRUCTURAL = /^(list|box|swatch|range|rating|hidden|collapsed|non-collapse|show-search-box|open-list|close-list|content.*|title.*|label|item.*|column-\d+|amount-\d+|multi-level-tag|single-list|multiple-list)$/;
  function optionSlugFromClasses(el) {
    var classes = (el.className || '').split(/\s+/);
    for (var i = 0; i < classes.length; i++) {
      var m = classes[i].match(/^boost-pfs-filter-option-(.+)$/);
      if (m && !OPTION_STRUCTURAL.test(m[1])) return m[1];
    }
    return '';
  }

  // Query params use prefixed keys (pf_t_material, pf_p_price…); map slug -> actual key.
  function paramKeyForSlug(slug) {
    if (!slug) return '';
    var params = getQueryParams();
    var keys = Object.keys(params);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].indexOf('pf_') === 0 && keys[i].replace(/^pf_[a-z]+_/, '') === slug) return keys[i];
    }
    return '';
  }

  /* ---------- mobile facet pills ---------- */
  function buildPills() {
    var host = document.getElementById('mt-filter-pills');
    var tree = document.getElementById('boost-pfs-filter-tree');
    if (!host || !tree) return;
    var groups = tree.querySelectorAll('.boost-pfs-filter-option');
    var params = getQueryParams();
    var html = '';
    var made = 0;
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.classList.contains('boost-pfs-filter-option-hidden')) continue;
      if (g.querySelector('.boost-pfs-filter-clear-all')) continue; // Boost's "Refine By" block, not a facet
      // The title/heading also carry selected-count spans; the clean facet name is the
      // first text node of the -title-text span.
      var titleEl = g.querySelector('.boost-pfs-filter-option-title-text')
        || g.querySelector('.boost-pfs-filter-option-title-heading')
        || g.querySelector('.boost-pfs-filter-option-title');
      var label = '';
      if (titleEl) {
        var textNode = titleEl.childNodes[0];
        label = (textNode && textNode.nodeType === 3 ? textNode.nodeValue : titleEl.textContent) || '';
        label = label.replace(/\s+/g, ' ').replace(/\(\d+\)/g, '').trim();
      }
      if (!label || /^refine by$/i.test(label)) continue;
      var slug = optionSlugFromClasses(g);
      var paramKey = paramKeyForSlug(slug);
      var count = paramKey ? toArray(params[paramKey]).length : 0;
      html += '<button type="button" class="mt-fv2-pill" data-mt-option="' + escapeHtml(slug) + '" aria-haspopup="dialog">'
        + escapeHtml(label)
        + (count ? '<span class="mt-fv2-pill-count">' + count + '</span>' : '')
        + '</button>';
      made++;
    }
    host.innerHTML = html;
  }

  /* ---------- applied-filter chips (mobile row + desktop row above grid) ---------- */
  function buildChips() {
    var params = getQueryParams();
    var entries = [];
    Object.keys(params).forEach(function (k) {
      if (k.indexOf('pf_') !== 0) return;
      toArray(params[k]).forEach(function (v) {
        entries.push({ key: k, value: String(v), label: formatValue(k, v) });
      });
    });
    var html = entries.map(function (en) {
      return '<button type="button" class="mt-fv2-chip" data-mt-key="' + escapeHtml(en.key)
        + '" data-mt-value="' + escapeHtml(en.value) + '" aria-label="Remove filter ' + escapeHtml(en.label) + '">'
        + escapeHtml(en.label) + '<span class="mt-fv2-chip-x" aria-hidden="true">&times;</span></button>';
    }).join('');
    if (entries.length > 1) {
      html += '<button type="button" class="mt-fv2-chip mt-fv2-chip-clear">Clear all</button>';
    }
    var topbar = document.querySelector('.mt-dgrid-topbar');
    ['mt-applied-filters', 'mt-applied-filters-desktop'].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      /* topbar mode retires the desktop chips row — applied state lives in the pills
         (counts + dark border) plus the Quince-style Clear All pill in the bar */
      var target = (topbar && id === 'mt-applied-filters-desktop') ? '' : html;
      if (el.innerHTML !== target) el.innerHTML = target;
    });
    syncClearAllPill(topbar, entries.length);
    /* applied-pill emphasis: the vendor count span exists on every option (empty until
       selected) — tag options that actually hold a selection */
    if (topbar) {
      topbar.querySelectorAll('.boost-pfs-filter-option').forEach(function (o) {
        var c = o.querySelector('.boost-pfs-filter-option-title-count');
        o.classList.toggle('mt-dgrid-pill-active', !!(c && c.textContent.trim()));
      });
    }
  }

  /* Quince's end-of-row "Clear All" pill: lives OUTSIDE the tree div (Boost wipes tree
     innerHTML each render), created once, toggled per render. */
  function syncClearAllPill(topbar, activeCount) {
    if (!topbar) return;
    var pill = topbar.querySelector('.mt-dgrid-clearall-pill');
    if (!pill) {
      var host = topbar.querySelector('.boost-pfs-container-default-box, .boost-pfs-container-default-fullwidth');
      var tree = host && host.querySelector('.boost-pfs-filter-tree-h');
      if (!host || !tree) return;
      pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'mt-dgrid-clearall-pill';
      pill.textContent = 'Clear All';
      pill.addEventListener('click', function (e) {
        /* the click must not reach Boost's document-level close-outside handler —
           with an open dropdown it auto-applies the still-checked values, instantly
           re-applying what we just cleared */
        e.stopPropagation();
        clearAllFilters();
      });
      host.insertBefore(pill, tree.nextSibling);
    }
    pill.hidden = activeCount === 0;
  }

  function removeFilterValue(key, value) {
    if (typeof window.FilterApi === 'undefined') return;
    var remaining = toArray(getQueryParams()[key]).filter(function (x) { return String(x) !== value; });
    window.FilterApi.setParam(key, remaining);
    window.FilterApi.setParam('page', 1);
    window.FilterApi.applyFilter('filter', { filterOptionId: key, filterValue: value });
    track('mt_fv2_chip_remove', { facet: key });
  }

  function clearAllFilters() {
    if (typeof window.FilterApi === 'undefined') return;
    var params = getQueryParams();
    Object.keys(params).forEach(function (k) {
      if (k.indexOf('pf_') === 0) window.FilterApi.setParam(k, []);
    });
    window.FilterApi.setParam('page', 1);
    window.FilterApi.applyFilter('filter');
    track('mt_fv2_chip_remove', { facet: 'all' });
  }

  /* ---------- bottom sheet ---------- */
  var tree = document.getElementById('boost-pfs-filter-tree');
  var backdrop = document.querySelector('.mt-fv2-backdrop');
  var sheetOpen = false;

  // Boost rebuilds the sheet header when the tree opens, resetting the title text —
  // re-patch on every render AND every open.
  function patchSheetChrome() {
    var sheetTitle = document.querySelector('.mt-fv2-tree .boost-pfs-filter-mobile-toolbar-middle');
    if (sheetTitle && /^refine by$/i.test(sheetTitle.textContent.trim())) {
      sheetTitle.textContent = 'Filters';
    }
    // Quince-style always-reachable "Clear all" in the pinned footer (Boost rebuilds the
    // footer on open, so re-inject whenever it's missing)
    var footer = document.querySelector('.mt-fv2-tree .boost-pfs-filter-mobile-footer');
    if (footer && !footer.querySelector('.mt-fv2-clear-link')) {
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'mt-fv2-clear-link';
      clear.textContent = 'Clear all';
      footer.insertBefore(clear, footer.firstChild);
    }
  }

  function syncSheetState() {
    if (!tree) return;
    var open = tree.classList.contains('boost-pfs-filter-tree-mobile-open');
    if (open === sheetOpen) return;
    sheetOpen = open;
    if (backdrop) backdrop.hidden = !open;
    document.documentElement.classList.toggle('mt-fv2-sheet-open', open);
    if (open) {
      patchSheetChrome();
      setTimeout(patchSheetChrome, 250);
      track('mt_fv2_sheet_open', {});
    }
  }

  if (tree) {
    new MutationObserver(syncSheetState).observe(tree, { attributes: true, attributeFilter: ['class'] });
  }

  function openSheet(optId) {
    if (!tree) return;
    if (!tree.classList.contains('boost-pfs-filter-tree-mobile-open')) {
      // Boost binds its open logic to the injected button inside the mobile-button wrapper.
      var btn = document.querySelector('.boost-pfs-filter-tree-mobile-button button')
        || document.querySelector('.boost-pfs-filter-tree-mobile-button');
      if (btn) btn.click();
    }
    if (!optId) return;
    // Boost re-renders while the sheet opens, so retry the expand until the
    // -content-open class sticks (style2 expands via the heading button).
    var attempts = 0;
    (function expand() {
      attempts++;
      var group = tree.querySelector('.boost-pfs-filter-option-' + optId);
      var content = group && group.querySelector('.boost-pfs-filter-option-content');
      if (content && !content.classList.contains('boost-pfs-filter-option-content-open')) {
        var t = group.querySelector('.boost-pfs-filter-option-title-heading');
        if (t) t.click();
      }
      if (content && content.classList.contains('boost-pfs-filter-option-content-open')) {
        group.scrollIntoView({ block: 'start' });
      } else if (attempts < 4) {
        setTimeout(expand, 300);
      }
    })();
  }

  function closeSheet() {
    if (!tree) return;
    var closeBtn = tree.querySelector('.boost-pfs-filter-show-result')
      || tree.querySelector('.boost-pfs-filter-close');
    if (closeBtn) { closeBtn.click(); return; }
    tree.classList.remove('boost-pfs-filter-tree-mobile-open');
    document.body.classList.remove('boost-pfs-filter-tree-open-body');
  }

  /* ---------- sort bottom sheet (Boost's dropdown gets clipped by the pill rail) ---------- */
  var sortSheet = null;
  var sortSheetOpen = false;

  function ensureSortSheet() {
    if (sortSheet) return sortSheet;
    sortSheet = document.createElement('div');
    sortSheet.className = 'mt-fv2-sort-sheet';
    sortSheet.setAttribute('role', 'listbox');
    sortSheet.hidden = true;
    document.body.appendChild(sortSheet);
    sortSheet.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.mt-fv2-sort-close')) { closeSortSheet(); return; }
      var row = e.target.closest && e.target.closest('.mt-fv2-sort-option');
      if (!row) return;
      // hand the choice to Boost's own (hidden) dropdown anchor so its engine applies it
      var anchors = document.querySelectorAll('.mt-fv2-sort .boost-pfs-filter-filter-dropdown a');
      for (var i = 0; i < anchors.length; i++) {
        if ((anchors[i].getAttribute('data-sort') || '') === row.getAttribute('data-mt-sort')) {
          anchors[i].click();
          break;
        }
      }
      track('mt_fv2_sort', { value: row.getAttribute('data-mt-sort') });
      closeSortSheet();
    });
    return sortSheet;
  }

  function openSortSheet() {
    var anchors = document.querySelectorAll('.mt-fv2-sort .boost-pfs-filter-filter-dropdown a');
    if (!anchors.length) return;
    var btn = document.querySelector('.mt-fv2-sort .boost-pfs-filter-top-sorting-wrapper');
    var currentLabel = btn ? btn.textContent.replace(/\s+/g, ' ').trim() : '';
    var html = '<button type="button" class="mt-fv2-sort-close" aria-label="Close">&times;</button>'
      + '<div class="mt-fv2-sort-sheet-title">Sort by</div>';
    for (var i = 0; i < anchors.length; i++) {
      var label = anchors[i].textContent.replace(/\s+/g, ' ').trim();
      var val = anchors[i].getAttribute('data-sort') || '';
      var selected = label === currentLabel;
      html += '<button type="button" role="option" class="mt-fv2-sort-option" data-mt-sort="' + escapeHtml(val) + '"'
        + ' aria-checked="' + (selected ? 'true' : 'false') + '">' + escapeHtml(label) + '</button>';
    }
    var sheet = ensureSortSheet();
    sheet.innerHTML = html;
    sheet.hidden = false;
    sortSheetOpen = true;
    if (backdrop) backdrop.hidden = false;
  }

  function closeSortSheet() {
    if (sortSheet) sortSheet.hidden = true;
    sortSheetOpen = false;
    if (backdrop && !sheetOpen) backdrop.hidden = true;
  }

  // Capture-phase intercept: on mobile the sort pill opens our sheet, never Boost's
  // dropdown. Clicks on the (hidden) dropdown anchors must pass through — that's how the
  // sort sheet forwards the chosen option to Boost.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.boost-pfs-filter-filter-dropdown')) return;
    if (e.target.closest('.mt-fv2-sort') && window.matchMedia('(max-width: 767.98px)').matches) {
      e.preventDefault();
      e.stopPropagation();
      openSortSheet();
    }
  }, true);

  if (backdrop) backdrop.addEventListener('click', function () {
    if (sortSheetOpen) { closeSortSheet(); return; }
    closeSheet();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (sortSheetOpen) { closeSortSheet(); return; }
    if (sheetOpen) closeSheet();
  });

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var pill = e.target.closest('.mt-fv2-pill');
    if (pill) {
      track('mt_fv2_pill_tap', { facet: pill.textContent.trim() });
      openSheet(pill.getAttribute('data-mt-option'));
      return;
    }
    var clear = e.target.closest('.mt-fv2-chip-clear, .mt-fv2-clear-link');
    if (clear) { clearAllFilters(); return; }

    var chip = e.target.closest('.mt-fv2-chip');
    if (chip) {
      removeFilterValue(chip.getAttribute('data-mt-key'), chip.getAttribute('data-mt-value'));
      return;
    }
    var subcat = e.target.closest('.mt-subcat__chip');
    if (subcat) {
      track('mt_fv2_subcat_click', { href: subcat.getAttribute('href') || '' });
      return;
    }
    var optItem = e.target.closest('.boost-pfs-filter-option-item');
    if (optItem && e.target.closest('.boost-pfs-filter-tree')) {
      var group = optItem.closest('.boost-pfs-filter-option');
      track('mt_fv2_filter_apply', { facet: group ? optionSlugFromClasses(group) : '' });
      return;
    }
    if (e.target.closest('.boost-pfs-filter-custom-sorting')) {
      track('mt_fv2_sort', {});
    }
  });

  /* ---------- post-render work (pills, chips, labels) ---------- */
  // The theme header is sticky (z-index 600, ~49px on mobile); the v2 toolbar sticks
  // directly beneath it. Measured at runtime because the header height can change.
  function setHeaderOffset() {
    var header = document.querySelector('#shopify-section-header-top');
    var h = header ? Math.round(header.getBoundingClientRect().height) : 49;
    document.documentElement.style.setProperty('--mt-fv2-top', h + 'px');
  }
  setHeaderOffset();
  window.addEventListener('resize', setHeaderOffset);

  function onBoostRender() {
    buildPills();
    buildChips();

    // Belt-and-braces: if the label config didn't reach Boost's Labels, patch the button
    // text and the sheet-header title.
    var mobileBtn = document.querySelector('.boost-pfs-filter-tree-mobile-button button');
    if (mobileBtn && /^(refine by|all filters)$/i.test(mobileBtn.textContent.trim())) {
      mobileBtn.textContent = 'Filter';
    }
    patchSheetChrome();

    // Desktop expand/collapse states are left to Boost's own settings — an auto-collapse
    // pass here raced Boost's active-filter auto-expand and produced inconsistent states.
  }

  function wirePrototypes() {
    if (typeof window.Filter === 'undefined' || !window.Filter.prototype) return false;
    var origAfterRender = window.Filter.prototype.afterRender;
    window.Filter.prototype.afterRender = function (data) {
      if (origAfterRender) origAfterRender.call(this, data);
      try { onBoostRender(data || this.data); } catch (err) { /* never break Boost's render */ }
    };
    return true;
  }

  // Defer order guarantees boost-pfs-filter.js already ran, but guard anyway.
  if (!wirePrototypes()) {
    document.addEventListener('DOMContentLoaded', wirePrototypes);
    window.addEventListener('load', wirePrototypes);
  }

  /* ---------- session tagging ---------- */
  try { if (typeof window.clarity === 'function') window.clarity('set', 'mt_filter_test', 'b'); } catch (e) { }
  track('mt_fv2_pageview', { page_type: /\/search/.test(location.pathname) ? 'search' : 'collection' });
})();

/* DESKTOP GRID 2026 topbar (2026-08-04): All Filters slide-over toggle. The panel markup
   only exists on topbar variant templates (dg_filter_layout == 'topbar') — this file also
   loads on the default template, so everything is guarded on the panel's presence. The
   panel hosts a third Boost tree (vertical, data-is-desktop) that the vendor keeps in
   sync with the pill bar and mobile sheet on every render. */
(function () {
  'use strict';
  var panel = document.getElementById('mt-dgrid-allfilters-panel');
  var bar = document.querySelector('.mt-dgrid-topbar');
  if (!panel || !bar) return;
  var btn = bar.querySelector('.mt-dgrid-allfilters-btn');
  var backdrop = document.querySelector('.mt-dgrid-allfilters-backdrop');
  function openPanel() {
    panel.classList.add('is-open');
    if (backdrop) backdrop.hidden = false;
    if (btn) btn.setAttribute('aria-expanded', 'true');
  }
  function closePanel() {
    panel.classList.remove('is-open');
    if (backdrop) backdrop.hidden = true;
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }
  if (btn) btn.addEventListener('click', function () {
    if (panel.classList.contains('is-open')) { closePanel(); } else { openPanel(); }
  });
  var closeBtn = panel.querySelector('.mt-dgrid-allfilters-close');
  if (closeBtn) closeBtn.addEventListener('click', closePanel);
  if (backdrop) backdrop.addEventListener('click', closePanel);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) closePanel();
  });
})();

/* Pinned filter bar on scroll (Quince): sticky is dead under the theme's
   overflow:hidden main wrapper — toggle fixed positioning + a spacer instead. */
(function () {
  'use strict';
  var bar = document.querySelector('.mt-dgrid-topbar');
  if (!bar || !window.matchMedia('(min-width: 768px)').matches) return;
  var spacer = document.createElement('div');
  spacer.style.display = 'none';
  bar.parentNode.insertBefore(spacer, bar.nextSibling);
  /* pin offset = live bottom of the theme's stacked sticky header sections
     (top strip + nav; ~123px on desktop, taller than the mobile-oriented CSS var) */
  function headerH() {
    var t = 0;
    ['#shopify-section-header-top', '#shopify-section-header'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return;
      var cs = getComputedStyle(el);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        var bRect = el.getBoundingClientRect().bottom;
        if (bRect > t) t = bRect;
      }
    });
    return t;
  }
  /* Fully LIVE pinning — no cached threshold, no frozen top offset. A fast scroll
     during page load used to capture the header's bottom mid-flight (announcement
     bars still in view, images still shifting layout) and freeze the bar with a gap
     below the header. Now every scroll frame re-reads the real geometry: the bar
     sticks when its natural position reaches the header, the pinned top self-corrects
     each frame, and the spacer (which holds the bar's natural slot) drives unsticking. */
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var h = headerH();
      if (!bar.classList.contains('mt-stuck')) {
        if (bar.getBoundingClientRect().top <= h - 2) {
          spacer.style.height = bar.offsetHeight + 'px';
          spacer.style.display = 'block';
          /* tuck 4px up under the header's bottom padding — tighter join, no pill
             clipping (the bar's own top padding absorbs the overlap) */
          bar.style.top = (h - 4) + 'px';
          bar.classList.add('mt-stuck');
        }
      } else {
        bar.style.top = (h - 4) + 'px';
        if (spacer.getBoundingClientRect().top > h - 2) {
          bar.classList.remove('mt-stuck');
          bar.style.top = '';
          spacer.style.display = 'none';
        }
      }
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  window.addEventListener('load', onScroll);
  setTimeout(onScroll, 400); /* browser-restored scroll positions pin immediately */
})();
